import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Plus, List, LayoutGrid } from "lucide-react";
import { CrmProvider, useCrm } from "./modal/CrmContext";
import OpportunityDetailModal from "./ui/OpportunityDetailModal";
import CreateOpportunityModal from "./ui/CreateOpportunityModal";
import KanbanBoard from "./ui/KanbanBoard";
import { StageBadge, DeadlineChip, ArtifactDot, formatDate, PipelineNotSetUp, GatesVerdictPill, gatesDetailLine, GATES_VERDICT_LABELS } from "./ui/crmUi";
import type { GatesFilter, GatesVerdict } from "./api/CrmApi";
import PageHeader from "shared/PageHeader";
import FilterChip from "shared/FilterChip";
import Button from "shared/Button";
import Alert from "shared/Alert";
import { FILLED_INPUT, FORM_CONTROL, TABLE_SHELL, TBODY_ROW, TD, TH, THEAD_ROW } from "shared/tableStyles";
import PagerNav from "shared/PagerNav";

function CrmContent() {
  const {
    pipelineLoaded, pipeline, opportunities, stageCounts, loading, error,
    page, totalPages, totalCount, activeStageKey, search, gatesFilter,
    setPage, setActiveStageKey, setSearch, setGatesFilter,
  } = useCrm();

  // Deep-link support: other pages (e.g. Prime Contacts' "RFP" column) link
  // here with ?opportunity_id=<id> to open that opportunity's detail modal
  // directly, instead of just landing on the unfiltered board/table.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkedId = searchParams.get("opportunity_id");
  const [detailId, setDetailId] = useState<number | null>(deepLinkedId ? Number(deepLinkedId) : null);
  useEffect(() => {
    if (deepLinkedId) setDetailId(Number(deepLinkedId));
  }, [deepLinkedId]);
  const [showCreate, setShowCreate] = useState(false);
  const [view, setView] = useState<"table" | "board">(() =>
    localStorage.getItem("hrms_crm_view") === "board" ? "board" : "table"
  );
  const switchView = (v: "table" | "board") => {
    setView(v);
    localStorage.setItem("hrms_crm_view", v);
    // The board renders every stage as a column, so a stage filter left over
    // from table view would silently mismatch the toolbar's opportunity count
    // (which reads the filtered context total) against what the board shows.
    if (v === "board" && activeStageKey) setActiveStageKey(null);
  };
  // Reload the board after the detail modal closes — it may have changed stages/milestones.
  const [boardVersion, setBoardVersion] = useState(0);

  // Debounce the search input — one request after typing pauses, not per keystroke.
  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== search) setSearch(searchInput);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput, search, setSearch]);

  const stages = pipeline?.stages ?? [];
  const allCount = Object.values(stageCounts).reduce((s, n) => s + n, 0);

  if (!pipelineLoaded) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!pipeline) {
    return <PipelineNotSetUp />;
  }

  return (
    <div className="flex flex-col gap-4 h-full min-h-0">
      {/* Stage filter chips (table view — the board shows every stage as a column) */}
      {view === "table" && (
      <div className="flex items-center gap-2 flex-wrap">
        <FilterChip active={activeStageKey === null} onClick={() => setActiveStageKey(null)} count={allCount}>
          All
        </FilterChip>
        {stages.map((s) => (
          <FilterChip
            key={s.key}
            active={activeStageKey === s.key}
            onClick={() => setActiveStageKey(activeStageKey === s.key ? null : s.key)}
            count={stageCounts[s.key]}
          >
            {s.name}
          </FilterChip>
        ))}
      </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className={`w-full pl-9 pr-3 py-2 ${FILLED_INPUT}`}
            placeholder="Search title, agency, solicitation #…"
          />
        </div>
        <select
          value={gatesFilter}
          onChange={(e) => setGatesFilter(e.target.value as GatesFilter)}
          title="Filter by the /rfp-gates qualification result"
          className={`px-3 py-1.5 h-9 ${FORM_CONTROL}`}
        >
          <option value="">Gates: any</option>
          {(Object.keys(GATES_VERDICT_LABELS) as GatesVerdict[]).map((v) => (
            <option key={v} value={v}>{GATES_VERDICT_LABELS[v]}</option>
          ))}
          <option value="none">Not checked</option>
        </select>
        <span className="text-xs text-muted-foreground">
          {totalCount} opportunit{totalCount === 1 ? "y" : "ies"}
        </span>
        <div className="flex items-center rounded-lg border border-border bg-surface overflow-hidden">
          <button
            onClick={() => switchView("table")}
            title="Table view"
            className={`p-2 transition-colors ${view === "table" ? "bg-primary-soft text-primary" : "text-muted-foreground hover:text-foreground"}`}
          >
            <List className="w-4 h-4" />
          </button>
          <button
            onClick={() => switchView("board")}
            title="Board view"
            className={`p-2 transition-colors ${view === "board" ? "bg-primary-soft text-primary" : "text-muted-foreground hover:text-foreground"}`}
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
        </div>
        <Button variant="primary" size="sm" onClick={() => setShowCreate(true)} className="ml-auto">
          <Plus className="w-4 h-4" />
          New Opportunity
        </Button>
      </div>

      {error && (
        <Alert tone="danger">
          {error}
        </Alert>
      )}

      {view === "board" ? (
        <KanbanBoard onOpen={setDetailId} version={boardVersion} />
      ) : (
      <>
      {/* Table */}
      <div className={`flex-1 overflow-auto ${TABLE_SHELL}`}>
        <table className="w-full text-sm">
          <thead>
            <tr className={THEAD_ROW}>
              <th className={TH}>Opportunity</th>
              <th className={TH}>Stage</th>
              <th className={TH}>Gates</th>
              <th className={TH}>Proposal Due</th>
              <th className={TH}>Artifacts</th>
              <th className={TH}>Updated</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center">
                  <div className="inline-block w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </td>
              </tr>
            )}
            {!loading && opportunities.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">
                  No opportunities{activeStageKey ? " in this stage" : ""} yet.
                </td>
              </tr>
            )}
            {!loading && opportunities.map((opp) => (
              <tr
                key={opp.id}
                onClick={() => setDetailId(opp.id)}
                className={`${TBODY_ROW} cursor-pointer`}
              >
                <td className={`${TD} max-w-md`}>
                  <p className="text-foreground font-medium truncate">{opp.title}</p>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">
                    {[opp.rfp?.agency_name, opp.rfp?.solicitation_number, opp.rfp?.location]
                      .filter(Boolean)
                      .join(" · ") || opp.source_type}
                  </p>
                  {opp.rfp?.folder_name && (
                    <p className="text-xs text-muted-foreground truncate mt-0.5">Folder: {opp.rfp.folder_name}</p>
                  )}
                </td>
                <td className={TD}>
                  <StageBadge stageKey={opp.stage_key} stageName={opp.stage_name} />
                </td>
                <td className={TD}>
                  <GatesVerdictPill verdict={opp.rfp?.gates_verdict} />
                  {gatesDetailLine(opp.rfp?.gates_verdict, opp.rfp?.gates_summary) && (
                    <p className="text-[11px] text-muted-foreground mt-0.5 whitespace-nowrap">
                      {gatesDetailLine(opp.rfp?.gates_verdict, opp.rfp?.gates_summary)}
                    </p>
                  )}
                </td>
                <td className={`${TD} text-foreground`}>
                  <DeadlineChip value={opp.rfp?.proposal_deadline} />
                </td>
                <td className={TD}>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <ArtifactDot done={!!opp.rfp?.page_built_at} label="Page" />
                    <ArtifactDot done={!!opp.rfp?.email_built_at} label="Email" />
                    <ArtifactDot done={!!opp.rfp?.primes_extracted_at} label="Primes" />
                    <ArtifactDot done={!!opp.rfp?.deployed_at} label="Live" />
                  </div>
                </td>
                <td className={`${TD} text-muted-foreground whitespace-nowrap`}>{formatDate(opp.updated_at, true)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <PagerNav page={page} totalPages={totalPages} onChange={setPage} />
      </>
      )}

      {detailId !== null && (
        <OpportunityDetailModal
          opportunityId={detailId}
          onClose={() => {
            setDetailId(null);
            setBoardVersion((v) => v + 1);
            if (searchParams.has("opportunity_id")) {
              const next = new URLSearchParams(searchParams);
              next.delete("opportunity_id");
              setSearchParams(next, { replace: true });
            }
          }}
        />
      )}
      {showCreate && <CreateOpportunityModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}

export default function CrmPage() {
  return (
    <CrmProvider>
      <div className="flex flex-col gap-5 h-full">
        <PageHeader
          title="SLED CRM"
          description="Tracks every decoded SLED RFP from intake through outreach, synced automatically by the rfp slash commands."
        />
        <CrmContent />
      </div>
    </CrmProvider>
  );
}
